import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import {
  AREAS,
  areaIcon,
  areaLabel,
  DutyNotice,
  dutyClashes,
  dutyText,
  levelLabel,
  levelRank,
  LEVELS,
  PermsByLevel,
  type DutyDef,
  type DutyPolicy,
  type Level,
} from './access.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useSave } from './common.tsx';
import { roleHint, roleLabel, roleOrder, type RoleInfo } from './Staff.tsx';

/**
 * Roles: the enterprise roles by department, the business's own roles (made by choosing an
 * access level per module: see, work, approve, set up), record rules ("people with this role see
 * only their own jobs"), and segregation of duties. The server checks all of it; the database
 * keeps the record rules, so they hold everywhere.
 */

interface Rule {
  model: string;
  field: string;
}
interface Role extends RoleInfo {
  rules: Rule[];
  people: number;
}
interface GroupPerm {
  name: string;
  label: string;
  level: Level;
  owner_only: boolean;
  yours: boolean;
}
interface Group {
  module: string;
  label: string;
  area: string;
  permissions: GroupPerm[];
}
interface Target {
  model: string;
  label: string;
  module: string;
  read: string | null;
  fields: { name: string; label: string }[];
}
interface PersonClash {
  id: string;
  name: string;
  username: string;
  roles: string[];
  conflicts: string[];
}
interface Data {
  roles: Role[];
  groups: Group[];
  targets: Target[];
  owner: boolean;
  duty_policy: DutyPolicy;
  duty_conflicts: DutyDef[];
  people_conflicts: PersonClash[];
}

/** Runs one of the roles operations: the business's own actions here, server administration's on /platform. */
export type AccessCall = <T = unknown>(
  op: 'roles' | 'save_role' | 'set_rules' | 'delete_role' | 'set_duty_policy',
  input: Record<string, unknown>,
) => Promise<T>;
const ownCall: AccessCall = (op, input) => action(`staff.${op}`, input);
const Call = createContext<AccessCall>(ownCall);

export function Roles() {
  return (
    <RolesWorkspace
      frame={(actions, body) => (
        <ManageLayout title={t('roles.title')} subtitle={t('roles.subtitle')} actions={actions}>
          {body}
        </ManageLayout>
      )}
    />
  );
}

/** The whole roles screen; /platform shows it for a business with its own call and frame. */
export function RolesWorkspace({ call = ownCall, frame }: { call?: AccessCall; frame: (actions: ReactNode, body: ReactNode) => ReactNode }) {
  const { fail } = useUi();
  const [data, setData] = useState<Data | null>(null);
  const callRef = useRef(call);
  callRef.current = call;
  const reload = useCallback(() => callRef.current<Data>('roles', {}).then(setData, fail), [fail]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const [editing, setEditing] = useState<{
    role: Role | null;
    copyOf?: Role;
  } | null>(null);
  const [rulesOf, setRulesOf] = useState<Role | null>(null);
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('all');
  const q = query.trim().toLowerCase();
  const matches = (r: Role) => !q || roleLabel(r.name, data?.roles).toLowerCase().includes(q) || roleHint(r).toLowerCase().includes(q);
  const own = (data?.roles ?? []).filter((r) => r.custom && matches(r)).sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
  const builtIn = (data?.roles ?? [])
    .filter((r) => !r.custom && (area === 'all' || r.area === area) && matches(r))
    .sort((a, b) => roleOrder(a.name) - roleOrder(b.name) || roleLabel(a.name).localeCompare(roleLabel(b.name)));
  const areas = AREAS.filter((a) => (data?.roles ?? []).some((r) => !r.custom && r.area === a));
  const saved = async () => {
    setEditing(null);
    setRulesOf(null);
    await reload();
  };
  return (
    <Call.Provider value={call}>
      {frame(
        <Button variant="primary" icon="plus" onClick={() => setEditing({ role: null })} disabled={!data}>
          {t('roles.new')}
        </Button>,
        <>
          {!data ? (
            <Skeleton h={320} r="var(--radius-lg)" />
          ) : (
            <div className="roles-page">
              <DutyCard data={data} onChanged={reload} />
              <div className="toolbar roles-toolbar">
                <div className="search grow">
                  <Icon name="search" size={18} />
                  <input
                    className="input"
                    type="search"
                    placeholder={t('access.find_role')}
                    aria-label={t('access.find_role')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <select className="input roles-area" value={area} onChange={(e) => setArea(e.target.value)} aria-label={t('access.department')}>
                  <option value="all">{t('access.all_departments')}</option>
                  {areas.map((a) => (
                    <option key={a} value={a}>
                      {areaLabel(a)}
                    </option>
                  ))}
                </select>
              </div>
              <section className="list-card" aria-labelledby="roles-own">
                <div className="list-card-head">
                  <div>
                    <h2 id="roles-own">{t('roles.yours')}</h2>
                    <p className="muted">{t('roles.yours_hint')}</p>
                  </div>
                </div>
                {own.length ? (
                  own.map((r) => <RoleRow key={r.name} role={r} roles={data.roles} policy={data.duty_policy} onOpen={() => setEditing({ role: r })} />)
                ) : q ? (
                  <p className="muted roles-none">{t('access.no_role_match')}</p>
                ) : (
                  <EmptyState
                    icon="users"
                    title={t('roles.none')}
                    body={t('roles.none_body')}
                    action={
                      <Button icon="plus" onClick={() => setEditing({ role: null })}>
                        {t('roles.new')}
                      </Button>
                    }
                  />
                )}
              </section>
              {(area === 'all' ? areas : areas.filter((a) => a === area)).map((a) => {
                const list = builtIn.filter((r) => r.area === a);
                if (!list.length) return null;
                return (
                  <section key={a} className="list-card" aria-labelledby={`roles-area-${a}`}>
                    <div className="list-card-head">
                      <div>
                        <h2 id={`roles-area-${a}`} className="roles-area-title">
                          <Icon name={areaIcon(a)} size={18} /> {areaLabel(a)}
                        </h2>
                        <p className="muted">{t(a === 'general' ? 'roles.built_in_hint' : 'access.area_roles_hint')}</p>
                      </div>
                    </div>
                    {list.map((r) => (
                      <RoleRow key={r.name} role={r} roles={data.roles} policy={data.duty_policy} onOpen={() => setRulesOf(r)} />
                    ))}
                  </section>
                );
              })}
              {q && !builtIn.length && !own.length ? <EmptyState icon="search" title={t('access.no_role_match')} /> : null}
            </div>
          )}
          {editing && data ? <RoleDialog data={data} role={editing.role} copyOf={editing.copyOf} onClose={() => setEditing(null)} onSaved={saved} /> : null}
          {rulesOf && data ? (
            <BuiltInDialog
              data={data}
              role={rulesOf}
              onClose={() => setRulesOf(null)}
              onSaved={saved}
              onCopy={() => {
                setRulesOf(null);
                setEditing({ role: null, copyOf: rulesOf });
              }}
            />
          ) : null}
        </>,
      )}
    </Call.Provider>
  );
}

const POLICIES: { key: DutyPolicy; icon: 'eyeOff' | 'alert' | 'lock' }[] = [
  { key: 'off', icon: 'eyeOff' },
  { key: 'warn', icon: 'alert' },
  { key: 'block', icon: 'lock' },
];

/** Segregation of duties: how the business handles them, and who holds duties kept apart. */
function DutyCard({ data, onChanged }: { data: Data; onChanged: () => Promise<unknown> }) {
  const call = useContext(Call);
  const { busy, run } = useSave();
  const set = (duties: DutyPolicy) =>
    void run(async () => {
      await call('set_duty_policy', { duties });
      await onChanged();
    }, t('duty.policy_saved'));
  const people = data.people_conflicts;
  return (
    <section className="list-card duty-card" aria-labelledby="duty-title">
      <div className="list-card-head">
        <div>
          <h2 id="duty-title" className="roles-area-title">
            <Icon name="shieldCheck" size={18} /> {t('duty.title')}
          </h2>
          <p className="muted">{t('duty.hint')}</p>
        </div>
      </div>
      <div className="duty-body">
        <fieldset className="duty-policies" disabled={!data.owner || busy}>
          <legend className="sr-only">{t('duty.policy')}</legend>
          {POLICIES.map((p) => (
            <label key={p.key} className={`duty-policy${data.duty_policy === p.key ? ' on' : ''}`}>
              <input type="radio" name="duty-policy" value={p.key} checked={data.duty_policy === p.key} onChange={() => set(p.key)} />
              <span className="duty-policy-icon" aria-hidden="true">
                <Icon name={p.icon} size={18} />
              </span>
              <span>
                <strong>
                  {t(`duty.policy_${p.key}`)}
                  {p.key === 'warn' ? <span className="tag">{t('duty.recommended')}</span> : null}
                </strong>
                <span className="muted small">{t(`duty.policy_${p.key}_hint`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {!data.owner ? <p className="muted small">{t('duty.owner_sets')}</p> : null}
        {data.duty_policy !== 'off' ? (
          people.length ? (
            <div className="duty-people">
              <h3>{tn('duty.people_n', people.length)}</h3>
              {people.map((p) => (
                <div key={p.id} className="duty-person">
                  <span className="list-main">
                    <span className="list-title">
                      {p.name} <span className="muted">@{p.username}</span>
                    </span>
                    <span className="list-sub">{p.roles.map((r) => roleLabel(r, data.roles)).join(' · ')}</span>
                  </span>
                  <ul>
                    {p.conflicts.map((k) => (
                      <li key={k}>
                        <Icon name="alert" size={14} /> {dutyText(k)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <p className="muted small">{t('duty.people_fix')}</p>
            </div>
          ) : (
            <p className="callout callout-ok small" role="status">
              <Icon name="checkCircle" size={16} /> {t('duty.people_none')}
            </p>
          )
        ) : null}
        <details className="role-perms">
          <summary>{tn('duty.pairs_n', data.duty_conflicts.length)}</summary>
          <ul>
            {data.duty_conflicts.map((d) => (
              <li key={d.key}>{dutyText(d.key)}</li>
            ))}
          </ul>
        </details>
      </div>
    </section>
  );
}

function RoleRow({ role: r, roles, policy, onOpen }: { role: Role; roles: Role[]; policy: DutyPolicy; onOpen: () => void }) {
  return (
    <button type="button" className="list-row role-row" onClick={onOpen}>
      <span className="role-row-icon" aria-hidden="true">
        <Icon name={r.custom ? 'sliders' : r.kind === 'department' ? areaIcon(r.area ?? 'general') : 'shield'} size={18} />
      </span>
      <span className="list-main">
        <span className="list-title">
          {roleLabel(r.name, roles)}
          {r.rules.length ? (
            <span className="tag tag-rule">
              <Icon name="lock" size={12} /> {t('roles.own_only')}
            </span>
          ) : null}
          {r.conflicts?.length && policy !== 'off' ? (
            <span className="tag tag-duty" title={r.conflicts.map(dutyText).join(' · ')}>
              <Icon name="alert" size={12} /> {tn('duty.role_tag', r.conflicts.length)}
            </span>
          ) : null}
        </span>
        <span className="list-sub">{roleHint(r)}</span>
      </span>
      <span className="role-row-meta muted small">
        <span>{tn('roles.permissions_n', r.permissions.length)}</span>
        <span>{tn('roles.people_n', r.people)}</span>
      </span>
      <Icon name="chevronRight" size={18} />
    </button>
  );
}

/** Which kinds of records people with these permissions see only their own of. */
function RulesEditor({ data, perms, rules, onChange }: { data: Data; perms: ReadonlySet<string>; rules: Rule[]; onChange: (r: Rule[]) => void }) {
  const targets = data.targets.filter((x) => !x.read || perms.has(x.read) || rules.some((r) => r.model === x.model));
  if (!targets.length) return <p className="muted small">{t('roles.rules_none')}</p>;
  const set = (model: string, field: string) => onChange([...rules.filter((r) => r.model !== model), ...(field ? [{ model, field }] : [])]);
  return (
    <div className="rules-list">
      {targets.map((x) => {
        const rule = rules.find((r) => r.model === x.model);
        const id = `rule-${x.model}`;
        return (
          <div key={x.model} className={`rule-row${rule ? ' on' : ''}`}>
            <label htmlFor={id} className="rule-kind">
              {rule ? <Icon name="lock" size={14} /> : <Icon name="eye" size={14} />}
              <span>{x.label}</span>
            </label>
            <select id={id} className="input" value={rule?.field ?? ''} onChange={(e) => set(x.model, e.target.value)}>
              <option value="">{t('roles.rule_all')}</option>
              {x.fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {t('roles.rule_theirs', { field: f.label.toLowerCase() })}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}

function RoleDialog({
  data,
  role,
  copyOf,
  onClose,
  onSaved,
}: {
  data: Data;
  role: Role | null;
  copyOf?: Role;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const call = useContext(Call);
  const start = role ?? copyOf;
  const [name, setName] = useState(role?.label ?? (copyOf ? t('roles.copy_name', { name: roleLabel(copyOf.name, data.roles) }) : ''));
  const [description, setDescription] = useState(role?.description ?? '');
  // A copy starts with only what the person may give.
  const giveable = useMemo(
    () => new Set(data.groups.flatMap((g) => g.permissions.filter((p) => p.yours && (data.owner || !p.owner_only)).map((p) => p.name))),
    [data],
  );
  const [perms, setPerms] = useState<Set<string>>(() => new Set((start?.permissions ?? []).map((p) => p.name).filter((p) => role || giveable.has(p))));
  const [rules, setRules] = useState<Rule[]>(start?.rules ?? []);
  const [query, setQuery] = useState('');
  const [tried, setTried] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const q = query.trim().toLowerCase();
  const groups = data.groups
    // A module that matches keeps all its permissions, so its level stays right.
    .filter((g) => !q || g.label.toLowerCase().includes(q) || g.permissions.some((p) => p.label.toLowerCase().includes(q)));
  const can = (p: { name: string; yours: boolean; owner_only: boolean }) => perms.has(p.name) || (p.yours && (data.owner || !p.owner_only));
  const toggle = (name: string) =>
    setPerms((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  const clashes = useMemo(() => dutyClashes(perms, data.duty_conflicts), [perms, data.duty_conflicts]);
  const startFrom = (key: string) => {
    const from = data.roles.find((r) => r.name === key);
    if (!from) return;
    setPerms(new Set(from.permissions.map((p) => p.name).filter((p) => giveable.has(p))));
    setRules(from.rules);
  };
  const bad = name.trim().length < 2;
  const save = async () => {
    setTried(true);
    if (bad) return;
    const ok = await run(
      () =>
        call('save_role', {
          key: role?.name ?? null,
          name: name.trim(),
          description: description.trim() || null,
          permissions: [...perms],
          rules,
        }),
      role ? t('roles.saved', { name: name.trim() }) : t('roles.created', { name: name.trim() }),
    );
    if (ok) await onSaved();
  };
  return (
    <Dialog
      title={role ? roleLabel(role.name, data.roles) : t('roles.new')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          {role ? (
            <Button variant="ghost" icon="trash" className="dialog-foot-start" onClick={() => setDeleting(true)}>
              {t('roles.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('roles.f_name')} required error={tried && bad ? t('roles.e_name') : null}>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('roles.f_description')} hint={t('roles.f_description_hint')}>
          <input className="input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>

      <section className="role-section" aria-labelledby="role-perms">
        <div className="role-section-head">
          <div>
            <h3 id="role-perms">{t('roles.what_can_do')}</h3>
            <p className="muted small">
              {tn('roles.chosen_n', perms.size)} · {t('access.matrix_hint')}
            </p>
          </div>
          <div className="search">
            <Icon name="search" size={16} />
            <input
              className="input"
              type="search"
              placeholder={t('roles.search')}
              aria-label={t('roles.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {!role ? (
          <Field label={t('access.start_from')} hint={t('access.start_from_hint')}>
            <select className="input" defaultValue="" onChange={(e) => startFrom(e.target.value)}>
              <option value="">{t('access.start_blank')}</option>
              {AREAS.map((a) => {
                const list = data.roles.filter((r) => !r.custom && r.area === a && r.name !== 'owner');
                return list.length ? (
                  <optgroup key={a} label={areaLabel(a)}>
                    {list.map((r) => (
                      <option key={r.name} value={r.name}>
                        {roleLabel(r.name, data.roles)}
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </select>
          </Field>
        ) : null}
        <DutyNotice clashes={clashes} policy={data.duty_policy} subject="role" />
        <AccessMatrix groups={groups} perms={perms} setPerms={setPerms} can={can} expandAll={!!q} toggle={toggle} />
        {!groups.length ? <p className="muted">{t('roles.no_match')}</p> : null}
      </section>

      <section className="role-section" aria-labelledby="role-rules">
        <h3 id="role-rules">{t('roles.rules')}</h3>
        <p className="muted small">{t('roles.rules_hint')}</p>
        <RulesEditor data={data} perms={perms} rules={rules} onChange={setRules} />
      </section>

      {deleting && role ? (
        <Confirm
          title={t('roles.delete_title', {
            name: roleLabel(role.name, data.roles),
          })}
          body={role.people ? tn('roles.delete_in_use', role.people) : t('roles.delete_body')}
          confirm={t('roles.delete')}
          danger
          busy={busy}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            void run(async () => {
              await call('delete_role', { key: role.name });
              setDeleting(false);
              await onSaved();
            }, t('roles.deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

type Choice = Level | 'none' | 'custom';
const rankOf = (p: GroupPerm) => levelRank(p.level);

/** The level a module is at in this role: the highest level with everything up to it chosen, or "custom". */
function levelOf(g: Group, perms: ReadonlySet<string>): Choice {
  const on = g.permissions.filter((p) => perms.has(p.name));
  if (!on.length) return 'none';
  const top = Math.max(...on.map(rankOf));
  const full = g.permissions.every((p) => rankOf(p) <= top === perms.has(p.name));
  if (!full) return 'custom';
  return LEVELS.find((l) => levelRank(l) === top) ?? 'custom';
}

/**
 * Access per module: No access · See · Work · Approve · Set up. A level gives everything up to it;
 * "Details" opens the module's permissions one by one.
 */
function AccessMatrix({
  groups,
  perms,
  setPerms,
  can,
  toggle,
  expandAll,
}: {
  groups: Group[];
  perms: ReadonlySet<string>;
  setPerms: (f: (s: Set<string>) => Set<string>) => void;
  can: (p: GroupPerm) => boolean;
  toggle: (name: string) => void;
  expandAll: boolean;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const setLevel = (g: Group, l: Level | 'none') =>
    setPerms((s) => {
      const n = new Set(s);
      for (const p of g.permissions) {
        if (!can(p)) continue;
        if (l !== 'none' && rankOf(p) <= levelRank(l)) n.add(p.name);
        else n.delete(p.name);
      }
      return n;
    });
  const byArea = [...AREAS, 'other']
    .map((a) => ({
      a,
      list: groups.filter((g) => ((AREAS as readonly string[]).includes(g.area) ? g.area === a : a === 'other')),
    }))
    .filter((x) => x.list.length);
  return (
    <div className="access-matrix">
      {byArea.map(({ a, list }) => (
        <section key={a} className="access-area" aria-label={areaLabel(a)}>
          <h4>
            <Icon name={areaIcon(a)} size={16} /> {areaLabel(a)}
          </h4>
          {list.map((g) => {
            const current = levelOf(g, perms);
            const levels = LEVELS.filter((l) => g.permissions.some((p) => rankOf(p) === levelRank(l)));
            const shown = expandAll || open.has(g.module);
            const name = `lvl-${g.module}`;
            return (
              <div key={g.module} className={`access-row${current === 'none' ? '' : ' on'}`}>
                <div className="access-row-main">
                  <span className="access-module">
                    {g.label}
                    {current === 'custom' ? <span className="tag">{t('access.custom')}</span> : null}
                  </span>
                  <div className="access-levels" role="radiogroup" aria-label={t('access.level_of', { module: g.label })}>
                    {(['none', ...levels] as (Level | 'none')[]).map((l) => {
                      const usable = l === 'none' || g.permissions.some((p) => rankOf(p) <= levelRank(l) && can(p));
                      return (
                        <label key={l} className={`access-level${current === l ? ' active' : ''}${usable ? '' : ' off'}`}>
                          <input type="radio" name={name} checked={current === l} disabled={!usable} onChange={() => setLevel(g, l)} />
                          <span>{l === 'none' ? t('access.level_none') : levelLabel(l)}</span>
                        </label>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="access-details"
                    aria-expanded={shown}
                    aria-controls={`perms-${g.module}`}
                    onClick={() =>
                      setOpen((o) => {
                        const n = new Set(o);
                        if (n.has(g.module)) n.delete(g.module);
                        else n.add(g.module);
                        return n;
                      })
                    }
                  >
                    {tn('access.details_n', g.permissions.length, {
                      n: g.permissions.filter((p) => perms.has(p.name)).length,
                      total: g.permissions.length,
                    })}
                    <Icon name={shown ? 'up' : 'down'} size={14} />
                  </button>
                </div>
                {shown ? (
                  <div className="access-perms" id={`perms-${g.module}`}>
                    {g.permissions.map((p) => {
                      const allowed = can(p);
                      return (
                        <label
                          key={p.name}
                          className={`perm-item${allowed ? '' : ' off'}`}
                          title={allowed ? undefined : p.owner_only ? t('roles.owner_only_hint') : t('roles.not_yours_hint')}
                        >
                          <input type="checkbox" checked={perms.has(p.name)} disabled={!allowed} onChange={() => toggle(p.name)} />
                          <span>{p.label}</span>
                          <span className={`level-tag level-${p.level === 'self' ? 'use' : p.level}`}>{levelLabel(p.level)}</span>
                          {p.owner_only ? <span className="tag tag-owner">{t('roles.owner_only')}</span> : null}
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function BuiltInDialog({
  data,
  role,
  onClose,
  onSaved,
  onCopy,
}: {
  data: Data;
  role: Role;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onCopy: () => void;
}) {
  const { busy, run } = useSave();
  const call = useContext(Call);
  const [rules, setRules] = useState<Rule[]>(role.rules);
  const perms = useMemo(() => new Set(role.permissions.map((p) => p.name)), [role]);
  const isOwner = role.name === 'owner';
  const save = async () => {
    const ok = await run(() => call('set_rules', { role: role.name, rules }), t('roles.saved', { name: roleLabel(role.name, data.roles) }));
    if (ok) await onSaved();
  };
  return (
    <Dialog
      title={roleLabel(role.name, data.roles)}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" icon="copy" className="dialog-foot-start" onClick={onCopy}>
            {t('roles.copy')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {!isOwner ? (
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          ) : null}
        </>
      }
    >
      <p className="role-dept muted">
        <Icon name={areaIcon(role.area ?? 'general')} size={16} /> {areaLabel(role.area ?? 'general')}
      </p>
      <p className="muted">{roleHint(role)}</p>
      {!isOwner ? <DutyNotice clashes={role.conflicts ?? []} policy={data.duty_policy === 'block' ? 'warn' : data.duty_policy} subject="role" /> : null}
      <section className="role-section" aria-labelledby="builtin-rules">
        <h3 id="builtin-rules">{t('roles.rules')}</h3>
        <p className="muted small">{t('roles.rules_hint')}</p>
        {isOwner ? <p className="muted small">{t('roles.owner_sees_all')}</p> : <RulesEditor data={data} perms={perms} rules={rules} onChange={setRules} />}
      </section>
      <details className="role-perms role-section">
        <summary>{tn('staff.what_role_can_do', role.permissions.length)}</summary>
        <PermsByLevel perms={role.permissions} />
      </details>
      <p className="callout callout-info small" role="note">
        <Icon name="info" size={16} /> {t('roles.built_in_fixed')}
      </p>
    </Dialog>
  );
}
